import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ShoppingBagIcon, ClockIcon, MessageSquareIcon } from 'lucide-react';
import { Perfume } from '../../data/retail';
import { money } from '../../data/food';
import { MerchantBadge } from '../MerchantBadge';
import { merchantFor } from '../../data/merchants';
import { PerfumeDetailModal } from './PerfumeDetailModal';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../AuthContext';
import type { ListingCardProps } from '../ListingCard';

/**
 * A fragrance can't be photographed — it is chosen on its notes and its
 * bottle size. The bottle sits on a ground tinted to the scent, and size is a
 * single priced choice so every card in the row is exactly the same height
 * whether the house sells two sizes or five.
 *
 * Not every house takes payment through Talab. Where they don't, the listing
 * stays fully browsable and the sale moves into conversation instead.
 */
export function PerfumeCard(props: ListingCardProps & {
  perfume: Perfume;
}) {
  const {
    id,
    title,
    location,
    images,
    perfume
  } = props;
  const {
    requireAuth
  } = useAuth();
  const {
    add,
    setOpen,
    lines
  } = useCart();
  const navigate = useNavigate();
  const [details, setDetails] = useState(false);
  const available = perfume.sizes.filter(s => s.inStock);
  const soldOut = perfume.sizes.filter(s => !s.inStock);
  const canOrder = perfume.ordering !== false && available.length > 0;
  const preferred = available.find(s => s.note) ?? available[0];
  const [sizeId, setSizeId] = useState(preferred?.id);
  const size = available.find(s => s.id === sizeId) ?? preferred;
  const house = location.split(' · ')[0];
  const inBag = lines.filter(l => l.listingId === id);
  const bagQty = inBag.reduce((n, l) => n + l.qty, 0);
  return <motion.div whileHover={{
    y: -4
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="transform-gpu bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-soft hover:shadow-medium transition-shadow duration-200 ease-out flex flex-col h-full group" data-id="element-3310" data-mp="32yw28j17mopc">
      {/* One frame, filled identically on every card */}
      {/* The same square frame the other result cards use, with the house and
          the concentration riding on the photo instead of taking body rows. */}
      <div className="relative aspect-square overflow-hidden" style={{
      backgroundColor: perfume.tint
    }} data-id="element-3311" data-mp="1rkqne7cvb87b">
        <img src={images[0]} alt={title} className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]" data-id="element-3312" data-mp="1mp1hlma98v2k" />
        <div className="absolute top-2 right-2" data-id="element-3313" data-mp="1vlahf2259vp8">
          <MerchantBadge merchant={merchantFor(perfume.house, 'Fragrance house')} variant="frosted" data-id="element-3314" data-mp="67nj7e5v9gd5" />
        </div>
        <div className="absolute bottom-2 left-2 flex items-center gap-1.5" data-id="element-3315" data-mp="1bgnee76pl8x">
          <span className="px-2 py-0.5 rounded-full bg-white/95 backdrop-blur-sm text-[10.5px] font-bold text-dark-navy shadow-sm" data-id="element-3316" data-mp="2cx929q67b89m">
            {perfume.concentration}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/95 backdrop-blur-sm text-[10.5px] font-bold text-slate-600 shadow-sm" data-id="element-3317" data-mp="243ce65th7lrk">
            <ClockIcon className="w-3 h-3 text-slate-400" data-id="element-3318" data-mp="1u6hkc085e9jm" />
            {perfume.longevity}
          </span>
        </div>
        {!canOrder && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-dark-navy/80 backdrop-blur-sm text-[10.5px] font-bold text-white" data-id="element-3319" data-mp="1z6vtjllhjpj8">
            Enquire
          </span>}
      </div>

      <div className="p-3 flex flex-col flex-1" data-id="element-3320" data-mp="1p7n1wze3en01">
        <div className="flex items-start justify-between gap-2" data-id="element-3321" data-mp="3fju3zn2kiq58">
          <h3 className="font-display font-bold text-[14.5px] leading-snug text-dark-navy line-clamp-1 min-w-0" data-id="element-3322" data-mp="3fhg8xe7w1dxo">
            {title}
          </h3>
          <span className="font-display font-bold text-[15px] text-dark-navy leading-snug flex-shrink-0" data-id="element-3323" data-mp="2uuruac88hk0h">
            {size && money(size.price)}
          </span>
        </div>
        {/* Notes are the description — nothing else explains a scent */}
        <p className="text-[11px] text-slate-400 mt-0.5 truncate" data-id="element-3324" data-mp="2kyf2jjw91g8d">
          {perfume.family} · {perfume.notes.join(', ')}
        </p>

        {/* One segmented row, the same height whether the house sells two
            sizes or five — the apparel size row's counterpart. */}
        {available.length > 0 && <div className="flex items-stretch gap-0.5 mt-2 p-0.5 bg-slate-100 rounded-lg" data-id="element-3325" data-mp="2732yglw7edes">
            {available.map(s => <button key={s.id} onClick={() => setSizeId(s.id)} title={s.note ? `${s.label} — ${s.note}` : s.label} className={`flex-1 min-w-0 px-1 py-1 rounded-md text-[11.5px] font-bold truncate transition-colors duration-150 ease-out ${sizeId === s.id ? 'bg-white text-dark-navy shadow-sm' : 'text-slate-500 hover:text-dark-navy'}`} data-id="element-3326" data-mp="1x79jywyxc9ud">
                {s.label}
              </button>)}
          </div>}

        <div className="mt-auto pt-2.5" data-id="element-3327" data-mp="cofmdxmzmdje">
          {bagQty > 0 ? <button onClick={() => setOpen(true)} className="w-full text-left mb-1.5 text-[11px] font-bold text-success hover:underline truncate" data-id="element-3328" data-mp="hns4k52wywfn">
              {bagQty} in your bag · view
            </button> : (size?.note || soldOut.length > 0) && <p className="text-[10.5px] text-slate-400 mb-1.5 truncate" data-id="element-3329" data-mp="3q33lpefaddkk">
                {size?.note ? size.note : `${soldOut.map(s => s.label).join(', ')} sold out`}
              </p>}

          {/* One action row on every card: the merchant's own next step,
              plus View details, which is always available. */}
          <div className="flex items-center gap-1.5" data-id="element-3330" data-mp="2ud3868fy5tkk">
            {canOrder ? <button onClick={() => requireAuth(() => size && add({
            listingId: id,
            merchant: house,
            title,
            image: images[0],
            summary: `${size.label} · ${perfume.concentration}`,
            unitPrice: size.price,
            qty: 1
          }))} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-dark-navy text-white text-[12.5px] font-bold rounded-lg hover:bg-slate-800 transition-colors duration-150 ease-out" data-id="element-3331" data-mp="2qbq0dstangb0">
                <ShoppingBagIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-3332" data-mp="lwfykt80k6v" />
                <span className="truncate" data-id="element-3333" data-mp="o8wqun8soic">Add to bag</span>
              </button> : <button onClick={() => requireAuth(() => navigate(`/messages?to=${encodeURIComponent(perfume.house)}&about=${encodeURIComponent(title)}`))} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-dark-navy text-white text-[12.5px] font-bold rounded-lg hover:bg-slate-800 transition-colors duration-150 ease-out" data-id="element-3334" data-mp="3rv36rhzxd3bk">
                <MessageSquareIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-3335" data-mp="21aisjb8gzprm" />
                <span className="truncate" data-id="element-3336" data-mp="21cmvnqyjokqu">Contact</span>
              </button>}
            <button onClick={() => requireAuth(() => setDetails(true))} className="px-2.5 py-2 text-[12.5px] font-semibold text-slate-500 rounded-lg hover:bg-slate-50 hover:text-dark-navy transition-colors duration-150 ease-out flex-shrink-0" data-id="element-3337" data-mp="1nivpmsk0vizv">
              Details
            </button>
          </div>
        </div>
      </div>

      {details && <PerfumeDetailModal id={id} title={title} location={location} images={images} perfume={perfume} initialSizeId={sizeId} onClose={() => setDetails(false)} data-id="element-3338" data-mp="kxuwa6ge4r04" />}
    </motion.div>;
}